/**
 * @file TypedSubscription — thin typed wrapper around `WsSubscription` that
 * validates every send + every receive against a `SubscriptionContract`'s
 * `clientFrame` / `serverFrame` schemas.
 *
 * Validation policy:
 * - **Outbound (`send`)**: client frame fails `clientFrame.safeParse` → throws
 *   `INVALID_CLIENT_FRAME`. Local typo / mis-shaped state surfaces as a thrown
 *   error at the call site, not a silent server-side `close(4001)`.
 * - **Inbound (`onData`)**: server frame fails `serverFrame.safeParse` → dropped
 *   silently and emitted via `onError` so a connected error handler observes
 *   the divergence. The wire-level WS stays open — only server-side
 *   frame-validation closes with `WS_CLOSE_INVALID_FRAME` (4001), and that
 *   lands in Phase 3 wiring (see umbrella #406 Q2).
 *
 * The wrapper does not replicate `WsSubscription`'s reconnect / request-
 * response correlation machinery — it delegates everything to the underlying
 * `WsSubscription` and just adds parsing.
 *
 * Binary channel: `sendBinary` / `onBinary` are pure pass-throughs to the
 * underlying `WsSubscription`. Binary frames are NOT validated against
 * `serverFrame` — they carry raw bytes (pty I/O is the canonical case)
 * whose shape isn't expressible as a JSON discriminated union. Typed
 * control envelopes ride `send` / `onData`; bytes ride `sendBinary` /
 * `onBinary`.
 */

import type { z } from 'zod'
import { CoreError } from '../shared/index.ts'
import { validateFrame } from '../shared/routing.ts'
import {
  WsSubscription,
  type WsMessage,
  type WsSubscriptionProps,
} from './ws_subscription.ts'
import type {
  BinaryCallback,
  CloseCallback,
  ErrorCallback,
  StatusCallback,
  SubscriptionStatus,
  Unsubscribe,
} from './types.ts'

/**
 * Frame inputs the typed client needs from a `SubscriptionContract` — a
 * minimal `Pick<...>` so test harnesses can pass a structural fake with only
 * the two schemas, without having to fabricate a full contract.
 *
 * @typeParam C - Inferred client-frame type (matches contract's `ClientFrame`).
 * @typeParam S - Inferred server-frame type (matches contract's `ServerFrame`).
 */
export type TypedSubscriptionContract<C, S> = {
  clientFrame: z.ZodType<C>
  serverFrame: z.ZodType<S>
}

/**
 * Typed WebSocket subscription client. Wraps `WsSubscription` and adds
 * `clientFrame` / `serverFrame` validation around every send + receive.
 *
 * @typeParam C - Client-frame union type (what the client sends).
 * @typeParam S - Server-frame union type (what the server pushes).
 */
export class TypedSubscription<C, S> {
  //////////////////////////////////////////////////////////////////////////////
  // Instance Properties
  //////////////////////////////////////////////////////////////////////////////

  private readonly ws: WsSubscription
  private readonly clientFrame: z.ZodType<C>
  private readonly serverFrame: z.ZodType<S>
  private readonly errorHandlers: ErrorCallback[] = []

  //////////////////////////////////////////////////////////////////////////////
  // Constructor
  //////////////////////////////////////////////////////////////////////////////

  /**
   * Construct a typed subscription. `WsSubscription` is constructed eagerly
   * (matching its existing contract) so callers see the connection attempt
   * begin on construction.
   *
   * @param contract - Source of the frame schemas. Pass a full
   *   `SubscriptionContract<...>` produced by `defineSubscription(...)`, or
   *   a `{ clientFrame, serverFrame }` pair for tests.
   * @param props - Forwarded to `WsSubscription` (url, reconnect timings, …).
   */
  constructor(
    contract: TypedSubscriptionContract<C, S>,
    props: WsSubscriptionProps,
  ) {
    this.clientFrame = contract.clientFrame
    this.serverFrame = contract.serverFrame
    this.ws = new WsSubscription(props)
  }

  //////////////////////////////////////////////////////////////////////////////
  // Status
  //////////////////////////////////////////////////////////////////////////////

  /** Current connection status (delegates to underlying WsSubscription). */
  get status(): SubscriptionStatus {
    return this.ws.status
  }

  /** Most recent connection error, if any. */
  get error(): CoreError | undefined {
    return this.ws.error
  }

  //////////////////////////////////////////////////////////////////////////////
  // Send
  //////////////////////////////////////////////////////////////////////////////

  /**
   * Send a typed client frame. Validates locally first; throws
   * `INVALID_CLIENT_FRAME` if `clientFrame.safeParse` fails. The thrown
   * `CoreError`'s `metadata.issues` carries the Zod issue list.
   *
   * Successful frames are JSON-encoded and pushed onto the underlying
   * `WsSubscription` send path — same delivery semantics as the untyped
   * client (silent drop if not yet connected).
   *
   * @throws CoreError with code `INVALID_CLIENT_FRAME` on schema failure.
   */
  send(frame: C): void {
    const result = validateFrame(this.clientFrame, frame)
    if (!result.ok) {
      throw new CoreError({
        code: 'INVALID_CLIENT_FRAME',
        message: 'client frame failed schema validation',
        metadata: { issues: result.issues },
      })
    }
    // The frame's `type` discriminator satisfies WsMessage; the
    // discriminated-union members shape-match WsMessage's open object.
    this.ws.send(result.value as unknown as WsMessage)
  }

  //////////////////////////////////////////////////////////////////////////////
  // Receive
  //////////////////////////////////////////////////////////////////////////////

  /**
   * Register a typed server-frame handler. Inbound frames are parsed with
   * `serverFrame.safeParse`; failures are dropped (the wire-level socket
   * stays open) and surfaced through `onError` so a connected error handler
   * observes the divergence.
   *
   * @returns Unsubscribe function that detaches this handler.
   */
  onData(handler: (frame: S) => void): Unsubscribe {
    return this.ws.onData(raw => {
      const result = validateFrame(this.serverFrame, raw)
      if (!result.ok) {
        const err = new CoreError({
          code: 'INVALID_SERVER_FRAME',
          message: 'server frame failed schema validation',
          metadata: { issues: result.issues, raw },
        })
        for (const onErr of this.errorHandlers) {
          try {
            onErr(err)
          } catch {
            // ignore handler errors
          }
        }
        return
      }
      handler(result.value)
    })
  }

  //////////////////////////////////////////////////////////////////////////////
  // Binary channel (pass-through, NOT validated)
  //////////////////////////////////////////////////////////////////////////////

  /**
   * Send a fire-and-forget binary frame on the underlying WebSocket.
   * Binary frames bypass the `clientFrame` validation channel — they
   * carry raw bytes (pty I/O, file uploads, …) whose shape is not
   * expressible as a JSON discriminated union. The terminal pty
   * bridge is the canonical consumer: text frames flow through `send`
   * with the typed control envelope (`resize`, `resume`); pty bytes
   * flow through `sendBinary` with no schema in the way.
   *
   * Silently drops the frame if the underlying socket is not OPEN —
   * same semantics as `WsSubscription.sendBinary`.
   */
  sendBinary(data: Uint8Array): void {
    this.ws.sendBinary(data)
  }

  /**
   * Register a handler for incoming binary frames. Binary frames are
   * NOT validated against `serverFrame` — they pass through as raw
   * `Uint8Array`s. The handler is the symmetric pair of
   * {@link sendBinary}; both channels exist so the pty bridge can
   * keep its byte-for-byte transport alongside the typed JSON
   * control envelope.
   *
   * @returns Unsubscribe function that detaches this handler.
   */
  onBinary(handler: BinaryCallback): Unsubscribe {
    return this.ws.onBinary(handler)
  }

  //////////////////////////////////////////////////////////////////////////////
  // Status / Error / Close / Lifecycle (pass-through)
  //////////////////////////////////////////////////////////////////////////////

  /**
   * Register a status-change handler. Pass-through to the underlying
   * `WsSubscription` — same eager-fire-with-current-status semantics.
   */
  onStatus(handler: StatusCallback): Unsubscribe {
    return this.ws.onStatus(handler)
  }

  /**
   * Register an error handler. Receives both wire errors (forwarded from
   * `WsSubscription.onError`) and inbound-frame validation failures (emitted
   * by this wrapper).
   */
  onError(handler: ErrorCallback): Unsubscribe {
    const fromWs = this.ws.onError(handler)
    this.errorHandlers.push(handler)
    return () => {
      fromWs()
      const i = this.errorHandlers.indexOf(handler)
      if (i !== -1) this.errorHandlers.splice(i, 1)
    }
  }

  /** Register a close handler. Pass-through to the underlying `WsSubscription`. */
  onClose(handler: CloseCallback): Unsubscribe {
    return this.ws.onClose(handler)
  }

  /** Close the WebSocket and stop reconnecting. */
  unsubscribe(): void {
    this.ws.unsubscribe()
  }
}
